import { DATA_ACCESS_SERVICE_TOKEN } from '@cgs/data-access';
import { wait } from '@cgs/shared';
import {
  isResumeDataDto,
  ResumeDataDto,
  TrifectaSpinResultResponse,
} from '../../../models/trifecta-dto';
import { TrifectaGameHook } from './trifecta-game-hook';
import { TrifectaMainDataAccessService } from '../services/trifecta-main-data-access.service';

export class TrifectaMainGameHook extends TrifectaGameHook {
  private readonly dataAccessService: TrifectaMainDataAccessService;

  constructor(...args: ConstructorParameters<typeof TrifectaGameHook>) {
    super(...args);

    this.dataAccessService =
      this.container.resolve<TrifectaMainDataAccessService>(DATA_ACCESS_SERVICE_TOKEN);
  }

  protected override onInsufficientFundsError(): void {
    this.dataAccessService.abortCurrentRequest();
  }

  protected async handlePlay(
    response: TrifectaSpinResultResponse | ResumeDataDto | Error,
    onSuccess: () => void,
    onError: (err: any) => void
  ): Promise<void> {
    if (response instanceof Error) {
      return onError(response.message);
    }

    let isBaseGameSpin: boolean;

    if (isResumeDataDto(response)) {
      this.gameResumed.emit();

      const lastSpin = response.spins[0];

      isBaseGameSpin = !!lastSpin && !lastSpin.freeSpinsInfo && !lastSpin.bonusInfo;

      if (isBaseGameSpin) {
        await wait(100);
        this.gameStateMachine.doSpin();
        await wait(0);
        this.dataAccessService.nextPlayResponse({ spin: lastSpin });
      }
    } else {
      this.dataAccessService.nextPlayResponse(response);
      this.checkAutoplayConditions(response.spin);

      isBaseGameSpin = !response.spin.freeSpinsInfo && !response.spin.bonusInfo;
    }

    if (isBaseGameSpin) {
      await this.roundFinished;
      await this.trifectaSDKAdapter.acknowledgeSpins();
    } else {
      // Base spins never take this branch: freeSpinsFinished awaits
      // endFreeSpins.leaved, which fires only in the free spins flow.
      //
      // This was done to make it easier for us to read spins.
      // If a pop-up appears about free spins ending, we immediately read them in the recovery window.
      // We'll no longer have to look at unfinished spins.
      const freeSpinsFinished = this.freeSpinsFinished;

      if (freeSpinsFinished) {
        await freeSpinsFinished;
        await this.trifectaSDKAdapter.acknowledgeSpins();
        await this.roundFinished;
      } else {
        await this.roundFinished;
        await this.trifectaSDKAdapter.acknowledgeSpins();
      }
    }

    onSuccess();
  }

  // TODO: type the response same as in handlePlay once Trifecta reworks the
  // gamble response shape on their side (SDK/backend refactoring)
  protected async handlePlayGamble(
    response: any,
    onSuccess: () => void,
    onError: (err: string) => void
  ): Promise<void> {
    if (response instanceof Error) {
      return onError(response.message);
    }

    if (!isResumeDataDto(response)) {
      this.dataAccessService.nextGambleResponse(response);
    }

    await this.trifectaSDKAdapter.acknowledgeSpins();

    const payout = response.spin?.spin?.gambleInfo?.result?.payout || 0;

    if (payout) {
      this.trifectaSDKAdapter.addWin(
        payout / Math.pow(10, this.trifectaSDKAdapter.launchParams.currencyDecimals)
      );
    }

    onSuccess();
  }

  protected get freeSpinsFinished(): Promise<unknown> | null {
    return this.gameStateMachine.endFreeSpins.leaved.first;
  }
}
